import type { CharacterChoice } from '../characters/characterSelection';
import { CollectibleType } from '../collectibles/CollectibleType';
import { GAME_CONFIG, type GameConfig } from '../config';
import type { UiConfig } from '../config/ui.config';
import { SEG_COLLAPSE_40M } from '../level/segments/definitions/SEG_COLLAPSE_40M';
import { el, formatNumber } from './dom';
import { ICONS, icon } from './icons';
import { closeOnBackdrop, menuButton, menuFooter } from './MenuParts';

/** One entry of the explanation: an optional picture (HUD icon), a bold name and its text. */
export interface HowToItem {
  readonly image?: string;
  readonly name: string;
  readonly text: string;
}

export interface HowToSection {
  readonly title: string;
  /** Line icon of the section heading (icons.ts markup). */
  readonly icon: string;
  readonly items: readonly HowToItem[];
}

/** The numbers the page explains – straight from the game configuration, so the text never drifts from the rules. */
export interface HowToFacts {
  readonly bottlesPerLife: number;
  readonly maxLives: number;
  readonly pointsPerMeter: number;
  readonly pointsPerBottle: number;
  readonly crateBottles: number;
  readonly goldenBottles: number;
  readonly toolboxBottles: number;
  readonly turboSeconds: number;
  readonly turboSpeed: number;
  readonly heapBottles: number;
  readonly pitBottles: number;
  readonly perfectLives: number;
  /** Start bonus of the quiz rounds and the run distance (m) each one begins at. */
  readonly quizRounds: readonly { readonly from: number; readonly bonus: number }[];
  readonly quizSeconds: number;
  /** Bottles off for a wrong answer / a mini game without a hit. */
  readonly wrongPenalty: number;
  /** Run distances (m) from which the run gets harder in steps. */
  readonly difficultySteps: readonly number[];
  /** Run distance (m) from which the house collapse can come. */
  readonly collapseFrom: number;
}

export function howToFacts(config: GameConfig, collapseFrom = SEG_COLLAPSE_40M.generation.minDistance ?? 0): HowToFacts {
  const value = config.collectibles.bottleValue;
  return {
    bottlesPerLife: config.scoring.bottlesPerExtraLife,
    maxLives: config.scoring.maxExtraLives,
    pointsPerMeter: config.scoring.pointsPerMeter,
    pointsPerBottle: config.scoring.pointsPerBottle,
    crateBottles: value[CollectibleType.BeerCrate],
    goldenBottles: value[CollectibleType.GoldenBottle],
    toolboxBottles: value[CollectibleType.Toolbox],
    turboSeconds: config.collectibles.turbo.seconds,
    turboSpeed: config.collectibles.turbo.speedFactor,
    heapBottles: config.dozer.rewards.bottlesPerHeap,
    pitBottles: config.dozer.rewards.pitFilledBottles,
    perfectLives: config.dozer.rewards.perfectExtraLives,
    quizRounds: config.quiz.rounds.map((round) => ({ from: round.fromDistance, bonus: round.bonus })),
    quizSeconds: config.quiz.timeLimitSeconds,
    wrongPenalty: config.quiz.wrongPenalty,
    // the named steps (since 2026-10-05 from the blocked lanes at 400 m on) – the first two tiers only ease the start in
    difficultySteps: config.difficulty.tiers.map((tier) => tier.fromDistance).filter((d) => d >= 400),
    collapseFrom,
  };
}

/** German number with up to one decimal ("2,5") – formatNumber floors, which would turn the 2.5 × turbo into "2". */
const decimal = (value: number): string => value.toLocaleString('de-DE', { maximumFractionDigits: 1 });
const km = (meters: number): string => decimal(meters / 1000);
const points = (n: number): string => `${n} ${n === 1 ? 'Punkt' : 'Punkte'}`;
const list = (parts: readonly string[]): string => (parts.length <= 1 ? parts.join('') : `${parts.slice(0, -1).join(', ')} und ${parts[parts.length - 1]}`);
const life = (n: number): string => (n === 1 ? '1 Extra-Leben' : `${n} Extra-Leben`);
/** "500 Flaschen, ab 2 km 750, ab 4 km 1.000" */
const quizRounds = (rounds: HowToFacts['quizRounds']): string =>
  rounds.every((round) => round.bonus === rounds[0]?.bonus)
    ? `${formatNumber(rounds[0]?.bonus ?? 0)} Flaschen`
    : rounds.map((round, i) => (i === 0 ? `${formatNumber(round.bonus)} Flaschen` : `ab ${km(round.from)} km ${formatNumber(round.bonus)}`)).join(', ');

/**
 * Everything a player should know (user 2026-10-03: "eine Infoseite … wie es funktioniert … was ist der Dumper, wenn man
 * da reinhüpft, was bekommt man für eine Kiste Bier"): goal, controls, pickups, the dozer, obstacles, junctions, lives,
 * points, the difficulty steps and the two characters.
 */
export function howToSections(facts: HowToFacts, characters: readonly CharacterChoice[]): HowToSection[] {
  const f = facts;
  return [
    {
      title: 'Das Ziel',
      icon: ICONS.distance,
      items: [
        {
          name: 'So weit laufen wie möglich',
          text: 'Du rennst über die Baustelle, weichst Hindernissen aus und sammelst Bier. Der Bauherr ist dir auf den Fersen – wer stolpert, lässt ihn aufholen.',
        },
      ],
    },
    {
      title: 'Steuerung',
      icon: ICONS.react,
      items: [
        { name: 'Handy', text: 'Wischen nach links/rechts: Spur wechseln · nach oben: springen, in der Luft noch einmal: Doppelsprung · nach unten: rutschen. Nach unten wischen und den Finger liegen lassen = Dauerrutschen, dabei seitlich bewegen = Spurwechsel.' },
        { name: 'Tastatur', text: 'A/D oder ←/→ Spur · W/↑ springen (nochmal: Doppelsprung) · S/↓ rutschen (halten = weiterrutschen) · Esc oder P Pause · im Quiz: ↑/↓ oder ←/→ wählen, Enter bestätigen (oder 1/2/3).' },
      ],
    },
    {
      title: 'Einsammeln',
      icon: ICONS.collect,
      items: [
        { image: '/assets/ui/hud/flasche.png', name: 'Bierflasche', text: `+1 Flasche. Alle ${formatNumber(f.bottlesPerLife)} Flaschen gibt es ein Extra-Leben (höchstens ${f.maxLives}).` },
        { image: '/assets/ui/hud/helm.png', name: 'Helm', text: 'Sofort +1 Extra-Leben.' },
        {
          image: '/assets/ui/hud/bierkiste.png',
          name: 'Bierkiste = Turbo',
          text: `${f.turboSeconds} Sekunden unverwundbar und ${decimal(f.turboSpeed)}-fach so schnell (+${f.crateBottles} Flaschen). Selten und schwer erreichbar – oben auf Containern oder ganz oben in hohen Flaschenbögen.`,
        },
        {
          name: 'Goldene Flasche: Quiz oder Minispiel',
          text: `Glänzt hoch in der Luft – nur mit Doppelsprung von der Rampe zu erreichen (+${f.goldenBottles} Flaschen). Danach per Zufall das Bau-Quiz (${f.quizSeconds} Sekunden Zeit, je schneller richtig, desto mehr Bonus) oder ein Minispiel: Kran, Hammer & Nagel oder Betonmischer – je genauer, desto mehr. Bonus bis zu ${quizRounds(f.quizRounds)}. Falsch oder daneben: −${f.wrongPenalty} Flaschen.`,
        },
        { name: 'Werkzeugkiste', text: `+${f.toolboxBottles} Flaschen.` },
      ],
    },
    {
      title: 'Die Raupe',
      icon: ICONS.trophy,
      items: [
        {
          name: 'Reinspringen und losschieben',
          text: `Spring auf die geparkte Planierraupe – dann fährst du sie und lenkst wie beim Laufen. Schieb die Erdhaufen mit dem Schild vor dir her: ${f.heapBottles} Flaschen je Haufen. Mit genug Erde füllst du am Ende die Grube: +${f.pitBottles} Flaschen. Alle Haufen geschoben = PERFEKT: +${life(f.perfectLives)}. Zu wenig Erde: Die Raupe hält am Rand und schleudert dich über die Grube – ohne Leben zu verlieren. Springen = absteigen; rammst du ein anderes Hindernis, wirft es dich ab – das zählt als Treffer.`,
        },
        {
          name: 'Der ructec-Bulli',
          text: 'Ab etwa 8 km wartet der ructec-Bulli, voll beladen: 500 m Kampf um die Straße. Ein roter Konkurrent drängt von hinten heran (rote Pfeile warnen) und drückt dich weg – lenk dagegen, in ihn hinein: Nach drei Treffern fliegt er von der Straße. Lässt du ihn drücken, schiebt er dich rüber – am Rand kippst du um und verlierst Flaschen. Blockst du seine Spur, rammt er dein Heck. Der Gegenverkehr hält auf dich zu: spät ausweichen! Jeder Crash kostet Ladung.',
        },
      ],
    },
    {
      title: 'Hindernisse',
      icon: ICONS.dodge,
      items: [
        { name: 'Springen', text: 'Über Leitwände, Leitkegel, Betonrohre, Big Bags, Paletten und Betonblöcke. Kabeltrommeln und Ziegelstapel sind höher – die schaffst du nur mit Doppelsprung.' },
        { name: 'Rutschen', text: 'Unter tiefen Rohren, Balken, hängenden Kabeln, der niedrigen Tür und den Gerüst-Querriegeln durch. Manches davon schaffst du auch mit Doppelsprung.' },
        { name: 'Spur wechseln', text: 'Leitpfeiltafeln sperren eine Spur. Einem Container weichst du aus – oder du springst mit Doppelsprung aufs Dach und läufst oben weiter, dort liegen oft Flaschen.' },
        { name: 'Pendelkran', text: 'Die schwenkende Betonlast hängt zu tief zum Drunterrutschen und ihr Seil zu hoch zum Drüberspringen: in die Spur wechseln, über der sie gerade nicht ist. Manchmal schwenken zwei bis vier hintereinander.' },
        { name: 'Kranlasten', text: 'Senkt der Kran Stahlträger quer über die Straße ab: drunter durchrutschen. Senkt er einen Betonblock in deine Spur: Spur wechseln.' },
        { name: 'Bagger, Stapler, Lkw', text: 'Der Bagger kippt Schutt in eine äußere Spur, Stapler und Lkw kommen dir in deiner Spur entgegen – rechtzeitig ausweichen.' },
        { name: 'Gruben', text: 'Drüber springen oder in eine freie Spur wechseln. Über die große Grube führt in der Mitte ein Stahlträger – darauf nicht die Spur wechseln. Wer hineinfällt, verliert ein Leben.' },
        { name: 'Hauseinsturz', text: `Ab ${km(f.collapseFrom)} km kann neben der Straße ein Rohbau einstürzen – den Trümmern ausweichen.` },
      ],
    },
    {
      title: 'Abzweigungen',
      icon: ICONS.chevronRight,
      items: [
        {
          name: 'Erst auf der Kreuzung wischen',
          text: 'Nach links oder rechts biegst du nur ab, wenn du wirklich auf der Kreuzung bist – vorher wechselst du nur die Spur. Eine verpasste Kurve kostet ein Leben.',
        },
      ],
    },
    {
      title: 'Leben & Punkte',
      icon: ICONS.survive,
      items: [
        { name: 'Getroffen?', text: 'Frontal gegen ein Hindernis, in die Grube, Kurve verpasst oder eingeholt: Mit einem Extra-Leben geht es weiter (kurz unverwundbar), ohne ist der Lauf vorbei.' },
        { name: 'Punkte', text: `${points(f.pointsPerMeter)} pro Meter + ${points(f.pointsPerBottle)} pro Flasche – auch für den Bonus aus Quiz und Raupe.` },
        {
          name: 'Es wird schwerer',
          text: `Ab ${list(f.difficultySteps.map(km))} km: mehr Hindernisse, mehr bewegliche Gefahren und weniger Flaschen.`,
        },
      ],
    },
    {
      title: 'Die Figuren',
      icon: ICONS.account,
      items: characters.map((choice) => ({ name: `${choice.name} – ${choice.perkTitle}`, text: `${choice.advantage} ${choice.disadvantage}` })),
    },
  ];
}

/** "So funktioniert’s" (opened from the start screen) – a page over the start scene like the leaderboard. */
export class HowToScreen {
  readonly element: HTMLDivElement;
  private readonly panel: HTMLElement;

  constructor(parent: HTMLElement, ui: UiConfig, characters: readonly CharacterChoice[], onBack: () => void, facts: HowToFacts = howToFacts(GAME_CONFIG)) {
    this.element = el('div', 'screen menu menu-sub menu-howto', undefined, parent);
    const panel = el('section', 'sub-panel howto-panel', undefined, this.element);
    this.panel = panel;
    panel.setAttribute('aria-labelledby', 'howto-title');
    const title = el('h2', 'sub-title', undefined, panel);
    title.id = 'howto-title';
    title.append(icon(ICONS.help, 'sub-title-icon'), 'So funktioniert’s');

    for (const section of howToSections(facts, characters)) {
      const block = el('section', 'howto-section', undefined, panel);
      const head = el('h3', 'howto-heading', undefined, block);
      head.append(icon(section.icon, 'howto-heading-icon'), section.title);
      const items = el('ul', 'howto-items', undefined, block);
      for (const item of section.items) {
        const row = el('li', item.image ? 'howto-item has-image' : 'howto-item', undefined, items);
        if (item.image) {
          const image = el('img', 'howto-image', undefined, row);
          image.src = item.image;
          image.alt = '';
          image.decoding = 'async';
        }
        const text = el('div', 'howto-text', undefined, row);
        el('strong', 'howto-name', item.name, text);
        el('span', undefined, item.text, text);
      }
    }

    const actions = el('div', 'sub-actions howto-actions', undefined, panel);
    menuButton(actions, 'Zurück', onBack, 'is-secondary');
    menuFooter(this.element, ui.footer);
    closeOnBackdrop(this.element, onBack);
  }

  scrollToTop(): void {
    this.panel.scrollTop = 0;
  }
}
